import styled from "styled-components";
import { responsive } from "../../utils/style";
import GridColumn from "../grid/GridColumn";
import GridRow from "../grid/GridRow";
import BrandModule from "../BrandModule";
import CallToActionModule from "../CallToActionModule";
import ReviewMarquee, { Review } from "../ReviewMarquee";
import ForwardPathsComponent from "../plp/ForwardPathsComponent";
import Footnotes from "../Footnotes";
import ExpertsCarousel from "../ExpertsCarousel";
import CategoryCarousel from "../CategoryCarousel";
import { IGatsbyImageData } from "gatsby-plugin-image";
import BrandValueProps from "../product/BrandValueProps";
import SocialMarquee, { Media } from "../SocialMarquee";
import ProductCarousel from "../ProductCarousel";
import HomepageHeroLeft from "../home/HomepageHeroLeft";
import HomepageHeroFull from "../home/HomepageHeroFull";

const PageWrapper = styled.div`
  padding-top: 52px;
  max-width: 100vw;
  overflow: hidden;
  position: relative;

  ${responsive.md`
    padding-top: 64px;
  `};
`;

const SunBurst1 = styled.div`
  position: absolute;
  z-index: -1;

  width: 896px;
  height: 896px;
  left: -628px;
  top: 10%;

  @media (min-width: 933px) {
    width: 1662px;
    height: 1662px;
    left: -1131px;
    top: 10%;
  }

  border-radius: 1662px;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(255, 214, 0, 0.8) 0%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const SunBurst2 = styled.div`
  position: absolute;
  z-index: -1;

  width: 896px;
  height: 896px;
  right: -628px;
  top: 40%;

  z-index: -1;

  @media (min-width: 933px) {
    width: 1662px;
    height: 1662px;
    right: -1131px;
    top: 30%;
  }

  border-radius: 1662px;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(255, 214, 0, 0.8) 0%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const PageGridContainer = styled.div.attrs({
  className: "r-grid-container",
})`
  gap: var(--spacing-5) !important;

  @media (min-width: 750px) {
    gap: var(--spacing-6) !important;
  }
`;

const moduleTypes = [
  "Hero",
  "Category Carousel",
  "Brand Value Props",
  "Brand Module",
  "Product Carousel",
  "Review Marquee",
  "Experts Carousel",
  "Call to Action",
  "Social Marquee",
];

export type VideoContent = {
  captions?: any;
  description?: {
    description: string;
  };
  stream?: {
    videoUpload: {
      playbackId: string;
    };
  };
  title: string;
  videoLength?: number;
  hasAudio?: boolean;
};

type Module = {
  type: (typeof moduleTypes)[number];
  placement?: "Left" | "Right" | "Fullwidth";
  headline?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  subhead?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  links?: {
    text: string;
    to: {
      path: string;
    };
    ariaLabel?: string;
    role?: string;
    tabIndex?: number;
    isExternal?: boolean;
  }[];
  reviews?: Review[];
  backgroundColor?: string;
  textColor?: string;
  image?: {
    gatsbyImageData: any;
    description: string;
  };
  imageMobile?: {
    gatsbyImageData: any;
    description: string;
  };
  video?: VideoContent;
  videoMobile?: VideoContent;
  valueProps?: {
    name: string;
    disclaimer: string;
    icon: {
      file: { url: string };
    };
  }[];
  productCategories?: {
    shortName: string;
    contentful_id: string;
    cardImage: {
      title: string;
      description: string;
      gatsbyImageData: IGatsbyImageData;
    };
    shopLandingReference: {
      slug: string;
    };
  }[];
  experts?: {
    name: string;
    title: string;
    titleSubhead: string;
    image: {
      title: string;
      gatsbyImageData: IGatsbyImageData;
    };
  }[];
  media?: Media[];
  products?: {
    contentful_id: string;
    name: {
      name: string;
    };
    summary: string;
    slug: string;
    sku: string;
    path: string;
    stockStatus: string;
    productSubhead: string;
    shopImages: {
      gatsbyImageData: IGatsbyImageData;
      description: string;
    }[];
    ritualProduct: {
      product_id: string;
      sku: string;
      in_stock: boolean;
      ritualPlans: {
        plan_id: string;
        amount: number;
        billing_type: string;
        currency: string;
        interval_count: number;
        base_price: number;
      }[];
    };
  }[];
};

type PropTypes = {
  footnotes: {
    childMarkdownRemark: {
      html: string;
    };
  };
  modules: Module[];
  currency: {
    symbol: string;
    type: string;
  };
  offset: number;
};

const HomepageTemplate = ({
  modules,
  footnotes,
  currency,
  offset,
}: PropTypes) => {
  const heroContent = modules.find((module) => module.type === "Hero");
  const heroPlacement = heroContent?.placement;
  return (
    <PageWrapper>
      <SunBurst1 />
      <SunBurst2 />
      {heroContent && heroPlacement === "Left" && (
        <HomepageHeroLeft
          offset={offset}
          headline={heroContent?.headline}
          subhead={heroContent?.subhead}
          links={heroContent?.links}
          textColor={heroContent?.textColor}
          backgroundColor={heroContent?.backgroundColor}
          image={heroContent?.image}
          imageMobile={heroContent?.imageMobile}
          video={heroContent?.video}
          videoMobile={heroContent?.videoMobile}
        />
      )}
      {heroContent && heroPlacement === "Fullwidth" && (
        <HomepageHeroFull
          offset={offset}
          headline={heroContent?.headline}
          subhead={heroContent?.subhead}
          links={heroContent?.links}
          image={heroContent?.image}
          imageMobile={heroContent?.imageMobile}
          video={heroContent?.video}
          videoMobile={heroContent?.videoMobile}
        />
      )}

      <PageGridContainer>
        {modules.map((module, index) => {
          const { type } = module;

          if (type === "Category Carousel") {
            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 12,
                  }}
                  m={{
                    size: 12,
                  }}
                >
                  <CategoryCarousel
                    headline={module?.headline}
                    productCategories={module?.productCategories}
                  />
                </GridColumn>
              </GridRow>
            );
          }

          if (type === "Brand Value Props") {
            if (!module?.valueProps) return null;

            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                    hide: true,
                  }}
                  s={{
                    size: 2,
                  }}
                  m={{
                    size: 2,
                  }}
                />
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 8,
                  }}
                  m={{
                    size: 8,
                  }}
                >
                  <BrandValueProps valueProps={module.valueProps} />
                </GridColumn>
                <GridColumn
                  xs={{
                    size: 6,
                    hide: true,
                  }}
                  s={{
                    size: 2,
                  }}
                  m={{
                    size: 2,
                  }}
                />
              </GridRow>
            );
          }

          if (type === "Brand Module") {
            return (
              <BrandModule
                video={module?.video}
                image={module?.image}
                headline={module?.headline}
                subhead={module?.subhead}
                links={module?.links}
                placement={module?.placement}
                key={index}
              />
            );
          }

          if (type === "Product Carousel") {
            if (!module?.products) return null;
            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 12,
                  }}
                  m={{
                    size: 12,
                  }}
                >
                  <ProductCarousel
                    headline={module?.headline}
                    link={module?.links?.[0]}
                    products={module.products}
                    currency={currency}
                  />
                </GridColumn>
              </GridRow>
            );
          }

          if (type === "Review Marquee") {
            if (!module?.reviews) return null;

            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 1,
                    hide: true,
                  }}
                  s={{
                    size: 1,
                  }}
                  m={{
                    size: 3,
                  }}
                />
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 10,
                  }}
                  m={{
                    size: 6,
                  }}
                >
                  <ReviewMarquee reviews={module?.reviews} />
                </GridColumn>
              </GridRow>
            );
          }

          if (type === "Experts Carousel") {
            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 12,
                  }}
                  m={{
                    size: 12,
                  }}
                >
                  <ExpertsCarousel
                    headline={module?.headline}
                    subhead={module?.subhead}
                    experts={module?.experts}
                  />
                </GridColumn>
              </GridRow>
            );
          }

          if (type === "Call to Action") {
            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                    hide: true,
                  }}
                  s={{
                    size: 3,
                  }}
                  m={{
                    size: 3,
                  }}
                />
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 6,
                  }}
                  m={{
                    size: 6,
                  }}
                >
                  <CallToActionModule
                    headline={module?.headline}
                    subhead={module?.subhead}
                    link={module?.links?.[0]}
                  />
                </GridColumn>
              </GridRow>
            );
          }

          if (type === "Social Marquee") {
            if (!module?.media) return null;
            return (
              <GridRow key={index}>
                <GridColumn
                  xs={{
                    size: 6,
                  }}
                  s={{
                    size: 12,
                  }}
                  m={{
                    size: 12,
                  }}
                >
                  <SocialMarquee media={module?.media} />
                </GridColumn>
              </GridRow>
            );
          }

          return null;
        })}
      </PageGridContainer>
      <ForwardPathsComponent className="homepage" category="Daily Health" />
      {footnotes && <Footnotes footnotes={footnotes} />}
    </PageWrapper>
  );
};

export default HomepageTemplate;
